import { useState, useEffect, useCallback } from "react";
import api from "../services/api";
import "./MariageList.css";
import MariageOptionsModal from "./MariageOptionsModal";


interface Invite {
  _id: string;
  nom: string;
  table: string;
  couple: string;
  telephone: string;
  uniqueToken: string;
  statut: string;
  boissonPreferee?: string;
  lienEnvoye?: boolean;
  dateEnvoi?: string;
}

interface Mariage {
  _id: string;
  bride: string;
  groom: string;
  date: string;
  dateISO: string;
  heure: string;
  province: string;
  photoBackground?: string;
  invitationImage?: string;
  boissonsSansAlcool?: string[];
  boissonsAvecAlcool?: string[];
}

interface BoissonStat {
  boisson: string;
  count: number;
}

interface Props {
  onSelectMariage?: (id: string) => void;
  readOnly?: boolean;
  canDelete?: boolean;
  filterProvince?: string;
}

interface MariagesResponse {
  success: boolean;
  mariages: Mariage[];
}

interface InvitesResponse {
  success: boolean;
  invites: Invite[];
}

interface ApiResponse {
  success: boolean;
  message?: string;
  deletedCount?: number;
}

interface UpdateInviteResponse {
  success: boolean;
  message?: string;
  invite?: Invite;
}

interface UserResponse {
  success: boolean;
  user: {
    _id: string;
    name: string;
    phone: string;
    role: string;
    province: string;
  };
}

interface DemandeResponse {
  success: boolean;
  message?: string;
}

const MariageList = ({ onSelectMariage, readOnly = false, canDelete = true, filterProvince }: Props) => {
  const [mariages, setMariages] = useState<Mariage[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMariage, setSelectedMariage] = useState<Mariage | null>(null);
  const [invites, setInvites] = useState<{[key: string]: Invite[]}>({});
  const [loadingInvites, setLoadingInvites] = useState<{[key: string]: boolean}>({});
  const [expandedMariage, setExpandedMariage] = useState<string | null>(null);
  const [showBoissonsStats, setShowBoissonsStats] = useState<{[key: string]: boolean}>({});
  const [editingInvite, setEditingInvite] = useState<{invite: Invite; mariageId: string} | null>(null);
  const [editForm, setEditForm] = useState({
    nom: '',
    table: '',
    couple: 'non',
    telephone: ''
  });
  const [currentUserRole, setCurrentUserRole] = useState<string>("");
  const [showDemandeModal, setShowDemandeModal] = useState<{mariageId: string; type: string} | null>(null);
  const [demandeRaison, setDemandeRaison] = useState("");
  const [sendingDemande, setSendingDemande] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredInvites, setFilteredInvites] = useState<Invite[]>([]);

  const generateInvitationLink = (token: string) => {
    return `https://mwaliko.click/invitation/${token}`;
  }

  const shareViaWhatsApp = (telephone: string, invitationLink: string, inviteNom: string, mariageId: string, inviteId: string, mariage: Mariage) => {
    if (!telephone || telephone.trim() === "") {
        alert("Ce numéro de téléphone n'est pas renseigné");
        return;
    }
    
    const cleanPhone = telephone.replace(/\s+/g, '').replace(/^\+/, '');
    
    const message = `*🎊 INVITATION AU MARIAGE 🎊*

Bonjour Mr/Mme/Couple ${inviteNom},

C'est avec une grande joie que nous vous invitons à célébrer l'union de 

*👰‍♀️ ${mariage.bride}* & *🤵‍♂️ ${mariage.groom}*

*🗓️ Date:* ${mariage.date} à ${mariage.heure}

*🔗 Lien d'invitation:* ${invitationLink}

*💝 ${mariage.bride} & ${mariage.groom}*`;
    
    const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
    
    marquerLienEnvoye(mariageId, inviteId);
  };

  const marquerLienEnvoye = async (mariageId: string, inviteId: string) => {
    try {
      const response = await api.patch<ApiResponse>(`/mariages/${mariageId}/invites/${inviteId}/mark-sent`);
      
      if (response.data.success) {
        setInvites(prev => ({
          ...prev,
          [mariageId]: prev[mariageId].map(invite => 
            invite._id === inviteId 
              ? { ...invite, lienEnvoye: true, dateEnvoi: new Date().toISOString() } 
              : invite
          )
        }));
      }
    } catch (error) {
      console.error("Erreur marquage lien envoyé:", error);
    }
  };

  const fetchMariages = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get<MariagesResponse>("/mariages");
      const data = response.data;
      
      if (data.success && Array.isArray(data.mariages)) {
        let filteredMariages = data.mariages;
        if (filterProvince) {
          filteredMariages = data.mariages.filter((m: Mariage) => m.province === filterProvince);
        }
        setMariages(filteredMariages);
      } else if (Array.isArray(data)) {
        let filteredMariages = data as unknown as Mariage[];
        if (filterProvince) {
          filteredMariages = (data as Mariage[]).filter((m: Mariage) => m.province === filterProvince);
        }
        setMariages(filteredMariages);
      } else {
        console.error("Format de réponse inattendu:", data);
        setMariages([]);
      }
      
      const userResponse = await api.get<UserResponse>("/auth/me");
      if (userResponse.data.success) {
        setCurrentUserRole(userResponse.data.user.role);
      }
    } catch (err) {
      console.error("Erreur chargement mariages:", err);
    } finally {
      setLoading(false);
    }
  }, [filterProvince]);

  const fetchInvites = async (mariageId: string) => {
    setLoadingInvites(prev => ({...prev, [mariageId]: true}));
    
    try {
      const response = await api.get<InvitesResponse>(`/mariages/${mariageId}/invites`);
      const data = response.data;
      
      if (data.success && Array.isArray(data.invites)) {
        setInvites(prev => ({...prev, [mariageId]: data.invites}));
      } else if (Array.isArray(data)) {
        setInvites(prev => ({...prev, [mariageId]: data as unknown as Invite[]}));
      }
    } catch (err) {
      console.error("Erreur chargement invités:", err);
    } finally {
      setLoadingInvites(prev => ({...prev, [mariageId]: false}));
    }
  };

  useEffect(() => {
    fetchMariages();
  }, [fetchMariages]);

  useEffect(() => {
    if (expandedMariage && invites[expandedMariage]) {
      const mariageInvites = invites[expandedMariage];
      const filtered = mariageInvites.filter(invite =>
        invite.nom.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (invite.table && invite.table.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (invite.telephone && invite.telephone.includes(searchTerm))
      );
      setFilteredInvites(filtered);
    } else {
      setFilteredInvites([]);
    }
  }, [searchTerm, expandedMariage, invites]);

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => {
        alert("Lien copié dans le presse-papier !");
      }).catch(() => {
        fallbackCopyToClipboard(text);
      });
    } else {
      fallbackCopyToClipboard(text);
    }
  };

  const fallbackCopyToClipboard = (text: string) => {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.position = 'fixed';
    textarea.style.opacity = '0';
    document.body.appendChild(textarea);
    
    try {
      textarea.select();
      document.execCommand('copy');
      alert("Lien copié dans le presse-papier !");
    } catch (err) {
      alert("Impossible de copier le lien. Sélectionnez-le manuellement.");
    } finally {
      document.body.removeChild(textarea);
    }
  };

  const exportStatsToPDF = async (mariage: Mariage, invitesList: Invite[], boissonsStats: BoissonStat[]) => {
    const { jsPDF } = await import('jspdf');
    const autoTable = (await import('jspdf-autotable')).default;
    
    const doc = new jsPDF();
    
    doc.setFontSize(22);
    doc.setTextColor(44, 62, 80);
    doc.text(`Mariage de ${mariage.bride} & ${mariage.groom}`, 20, 20);
    
    doc.setFontSize(12);
    doc.setTextColor(100, 100, 100);
    doc.text(`Date: ${mariage.date} à ${mariage.heure}`, 20, 30);
    
    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.text('Résumé des invités', 20, 45);
    
    let finalY = 50;
    
    autoTable(doc, {
      startY: finalY,
      head: [['Indicateur', 'Valeur']],
      body: [
        ['Total invités', invitesList.length.toString()],
        ['Couples', invitesList.filter(i => i.couple === 'oui').length.toString()],
        ['Individus', invitesList.filter(i => i.couple === 'non').length.toString()],
        ['Confirmés', invitesList.filter(i => i.statut === 'confirmed').length.toString()],
        ['Déclinés', invitesList.filter(i => i.statut === 'declined').length.toString()],
        ['En attente', invitesList.filter(i => i.statut === 'pending').length.toString()],
        ['Liens envoyés', invitesList.filter(i => i.lienEnvoye).length.toString()]
      ],
      theme: 'grid',
      headStyles: { fillColor: [44, 62, 80] },
      columnStyles: { 0: { fontStyle: 'bold' } }
    });

    finalY = (doc as any).lastAutoTable.finalY + 15;

    if (boissonsStats.length > 0) {
      doc.setFontSize(16);
      doc.setTextColor(44, 62, 80);
      doc.text('Statistiques des boissons', 20, finalY);
      
      finalY += 5;
      
      autoTable(doc, {
        startY: finalY,
        head: [['Boisson', 'Nombre de personnes']],
        body: boissonsStats.map(item => [item.boisson, item.count.toString()]),
        theme: 'grid',
        headStyles: { fillColor: [106, 76, 147] }
      });
      
      finalY = (doc as any).lastAutoTable.finalY + 10;
      
      const totalAvecChoix = boissonsStats.reduce((sum, item) => sum + item.count, 0);
      const totalSansChoix = invitesList.length - totalAvecChoix;
      
      doc.setFontSize(12);
      doc.setTextColor(100, 100, 100);
      doc.text(`Total avec choix: ${totalAvecChoix}`, 20, finalY);
      doc.text(`Total sans choix: ${totalSansChoix}`, 20, finalY + 7);
      
      finalY += 20;
    }

    doc.setFontSize(16);
    doc.setTextColor(44, 62, 80);
    doc.text('Liste des invités', 20, finalY);
    
    finalY += 5;
    
    autoTable(doc, {
      startY: finalY,
      head: [['Nom', 'Table', 'Type', 'Statut', 'Boisson', 'Téléphone']],
      body: invitesList.map(invite => [
        invite.nom,
        invite.table || '-',
        invite.couple === 'oui' ? 'Couple' : 'Individuel',
        invite.statut === 'confirmed' ? 'Confirmé' : invite.statut === 'declined' ? 'Décliné' : 'En attente',
        invite.boissonPreferee || '-',
        invite.telephone || '-'
      ]),
      theme: 'grid',
      headStyles: { fillColor: [52, 73, 94] },
      styles: { fontSize: 9 }
    });

    doc.save(`statistiques-${mariage.bride}-${mariage.groom}.pdf`);
  };

  const toggleMariageExpansion = (mariageId: string) => {
    if (expandedMariage === mariageId) {
      setExpandedMariage(null);
      setShowBoissonsStats(prev => ({...prev, [mariageId]: false}));
      setSearchTerm("");
    } else {
      setExpandedMariage(mariageId);
      fetchInvites(mariageId);
      setSearchTerm("");
    }
  };

  const toggleBoissonsStats = (mariageId: string) => {
    setShowBoissonsStats(prev => ({...prev, [mariageId]: !prev[mariageId]}));
  };

  const getCardColor = (dateISO: string) => {
    if (!dateISO) return "gray";
    const today = new Date();
    const mariageDate = new Date(dateISO);
    if (today.toDateString() === mariageDate.toDateString()) {
      return "orange";
    } else if (today < mariageDate) {
      return "#28a745";
    } else {
      return "red";
    }
  };

  const shareViaEmail = (subject: string, body: string) => {
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.location.href = mailtoUrl;
  };

  const getStatusColor = (statut: string) => {
    switch(statut) {
      case 'confirmed': return '#28a745';
      case 'declined': return '#dc3545';
      default: return '#ffc107';
    }
  };

  const getStatusText = (statut: string) => {
    switch(statut) {
      case 'confirmed': return 'Confirmé';
      case 'declined': return 'Décliné';
      default: return 'En attente';
    }
  };

  const deleteInvite = async (inviteId: string, mariageId: string) => {
    if (readOnly) return;
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet invité ?")) {
      return;
    }

    try {
      const response = await api.delete<ApiResponse>(`/mariages/${mariageId}/invites/${inviteId}`);
      
      if (response.data.success) {
        alert("Invité supprimé avec succès");
        fetchInvites(mariageId);
      } else {
        alert("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const deleteAllInvites = async (mariageId: string) => {
    if (readOnly) return;
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer TOUS les invités de ce mariage ? Cette action est irréversible.")) {
      return;
    }

    try {
      const response = await api.delete<ApiResponse>(`/mariages/${mariageId}/invites`);
      
      if (response.data.success) {
        alert(`${response.data.deletedCount} invités supprimés avec succès`);
        setInvites(prev => ({...prev, [mariageId]: []}));
      } else {
        alert("Erreur lors de la suppression");
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de la suppression");
    }
  };

  const openEditInvite = (invite: Invite, mariageId: string) => {
    setEditingInvite({ invite, mariageId });
    setEditForm({
      nom: invite.nom,
      table: invite.table || '',
      couple: invite.couple,
      telephone: invite.telephone || ''
    });
  };

  const saveInviteEdit = async () => {
    if (!editingInvite) return;
    
    try {
      const response = await api.put<UpdateInviteResponse>(`/mariages/${editingInvite.mariageId}/invites/${editingInvite.invite._id}`, editForm);
      
      if (response.data.success) {
        alert('Invité modifié avec succès !');
        setEditingInvite(null);
        fetchInvites(editingInvite.mariageId);
      } else {
        alert('Erreur lors de la modification');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la modification');
    }
  };

  const handleDemandeSuppression = async () => {
    if (!showDemandeModal) return;
    if (!demandeRaison.trim()) {
      alert("Veuillez entrer une raison pour la demande");
      return;
    }

    setSendingDemande(true);
    try {
      const response = await api.post<DemandeResponse>(`/demandes/mariage/${showDemandeModal.mariageId}/suppression`, {
        raison: demandeRaison
      });

      if (response.data.success) {
        alert("Demande de suppression envoyée au megaadmin");
        setShowDemandeModal(null);
        setDemandeRaison("");
      } else {
        alert("Erreur lors de l'envoi de la demande");
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de l'envoi de la demande");
    } finally {
      setSendingDemande(false);
    }
  };

  const handleDemandeSuppressionInvites = async () => {
    if (!showDemandeModal) return;
    if (!demandeRaison.trim()) {
      alert("Veuillez entrer une raison pour la demande");
      return;
    }

    setSendingDemande(true);
    try {
      const response = await api.post<DemandeResponse>(`/demandes/mariage/${showDemandeModal.mariageId}/suppression-invites`, {
        raison: demandeRaison
      });

      if (response.data.success) {
        alert("Demande de suppression de tous les invités envoyée au megaadmin");
        setShowDemandeModal(null);
        setDemandeRaison("");
      } else {
        alert("Erreur lors de l'envoi de la demande");
      }
    } catch (error) {
      console.error("Erreur:", error);
      alert("Erreur lors de l'envoi de la demande");
    } finally {
      setSendingDemande(false);
    }
  };

  const getLocalBoissonsStats = (mariageId: string): BoissonStat[] => {
    const mariageInvites = invites[mariageId] || [];
    const boissonsStatsMap: {[key: string]: number} = {};
    
    mariageInvites.forEach(invite => {
      if (invite.boissonPreferee && invite.boissonPreferee.trim() !== "") {
        boissonsStatsMap[invite.boissonPreferee] = (boissonsStatsMap[invite.boissonPreferee] || 0) + 1;
      }
    });
    
    return Object.entries(boissonsStatsMap)
      .map(([boisson, count]) => ({ boisson, count }))
      .sort((a, b) => b.count - a.count);
  };

  const renderDemandeModal = () => {
    if (!showDemandeModal) return null;

    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div className="modal-content" style={{
          background: 'white',
          padding: '30px',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%'
        }}>
          <h2 style={{ marginTop: 0, color: '#333' }}>Demande de suppression</h2>
          
          <p style={{ marginBottom: '20px', color: '#666' }}>
            {showDemandeModal.type === "mariage" 
              ? "Vous êtes sur le point de demander la suppression de ce mariage. Cette action nécessite l'approbation du megaadmin."
              : "Vous êtes sur le point de demander la suppression de tous les invités de ce mariage. Cette action nécessite l'approbation du megaadmin."}
          </p>
          
          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Raison de la demande *
            </label>
            <textarea
              value={demandeRaison}
              onChange={(e) => setDemandeRaison(e.target.value)}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                minHeight: '100px',
                resize: 'vertical'
              }}
              placeholder="Expliquez pourquoi vous souhaitez supprimer ceci..."
              disabled={sendingDemande}
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                setShowDemandeModal(null);
                setDemandeRaison("");
              }}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
              disabled={sendingDemande}
            >
              Annuler
            </button>
            <button
              onClick={showDemandeModal.type === "mariage" ? handleDemandeSuppression : handleDemandeSuppressionInvites}
              style={{
                padding: '10px 20px',
                backgroundColor: '#dc3545',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: sendingDemande ? 'not-allowed' : 'pointer',
                opacity: sendingDemande ? 0.6 : 1
              }}
              disabled={sendingDemande}
            >
              {sendingDemande ? "Envoi en cours..." : "Envoyer la demande"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!editingInvite) return null;
    
    return (
      <div className="modal-overlay" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.5)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000
      }}>
        <div className="modal-content" style={{
          background: 'white',
          padding: '30px',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '90%',
          maxHeight: '80vh',
          overflowY: 'auto'
        }}>
          <h2 style={{ marginTop: 0, color: '#333' }}>Modifier l'invité</h2>
          
          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Nom complet *
            </label>
            <input
              type="text"
              value={editForm.nom}
              onChange={(e) => setEditForm({...editForm, nom: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              required
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Table
            </label>
            <input
              type="text"
              value={editForm.table}
              onChange={(e) => setEditForm({...editForm, table: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            />
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Type
            </label>
            <select
              value={editForm.couple}
              onChange={(e) => setEditForm({...editForm, couple: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
            >
              <option value="non">Individuel</option>
              <option value="oui">Couple</option>
            </select>
          </div>

          <div style={{ marginBottom: '15px' }}>
            <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
              Téléphone
            </label>
            <input
              type="tel"
              value={editForm.telephone}
              onChange={(e) => setEditForm({...editForm, telephone: e.target.value})}
              style={{
                width: '100%',
                padding: '10px',
                border: '1px solid #ddd',
                borderRadius: '4px'
              }}
              placeholder="ex: +243812345678"
            />
          </div>

          <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
            <button
              onClick={() => setEditingInvite(null)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Annuler
            </button>
            <button
              onClick={saveInviteEdit}
              style={{
                padding: '10px 20px',
                backgroundColor: '#c49f59',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Enregistrer
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderMariageItem = (m: Mariage) => {
    const isExpanded = expandedMariage === m._id;
    const mariageInvites = invites[m._id] || [];
    const isLoading = loadingInvites[m._id];
    const localBoissonsStats = getLocalBoissonsStats(m._id);
    const showStats = showBoissonsStats[m._id];
    const totalAvecChoix = localBoissonsStats.reduce((sum, item) => sum + item.count, 0);
    const totalSansChoix = mariageInvites.length - totalAvecChoix;
    const pourcentageAvecChoix = mariageInvites.length > 0 
      ? Math.round((totalAvecChoix / mariageInvites.length) * 100) 
      : 0;

    const invitesToShow = searchTerm && isExpanded ? filteredInvites : mariageInvites;

    return (
      <li key={m._id} className="mariage-item">
        <div className="content-zone">
          <div className="mariage-header">
            <strong className="mariage-names">{m.bride} & {m.groom}</strong>
            <span className="mariage-date">{m.date}</span>
          </div>
          
          <div className="meta">
            <span className="meta-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM7 10h5v5H7v-5z" fill="currentColor"/>
              </svg>
              {m.dateISO}
            </span>
            <span className="meta-item">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67V7z" fill="currentColor"/>
              </svg>
              {m.heure}
            </span>
          </div>

          <div className="mariage-images">
            {m.photoBackground && (
              <img
                src={`${process.env.REACT_APP_API_URL}/uploads/${m.photoBackground}`}
                alt="Fond mariage"
                className="mariage-img"
              />
            )}
            {m.invitationImage && (
              <img
                src={`${process.env.REACT_APP_API_URL}/uploads/${m.invitationImage}`}
                alt="Invitation mariage"
                className="mariage-img"
              />
            )}
          </div>

          <div className="action-buttons">
            {!readOnly && currentUserRole === "megaadmin" && (
              <button
                className="options-btn"
                onClick={() => setSelectedMariage(m)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
                </svg>
                Options
              </button>
            )}

            {!readOnly && currentUserRole === "super_admin" && (
              <>
                <button
                  className="demande-btn"
                  onClick={() => setShowDemandeModal({ mariageId: m._id, type: "mariage" })}
                  style={{
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '14px'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                  </svg>
                  Demander suppression
                </button>
                <button
                  className="demande-invites-btn"
                  onClick={() => setShowDemandeModal({ mariageId: m._id, type: "invites" })}
                  style={{
                    backgroundColor: '#fd7e14',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 12px',
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '5px',
                    fontSize: '14px'
                  }}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                  </svg>
                  Supprimer tous les invités
                </button>
              </>
            )}
            
            <button
              className="invites-btn"
              onClick={() => toggleMariageExpansion(m._id)}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
              </svg>
              {isExpanded ? 'Masquer' : 'Voir'} invités ({mariageInvites.length})
            </button>

            {onSelectMariage && (
              <button
                className="assign-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectMariage(m._id);
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20 3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H4V5h16v14zM8 10h8v2H8v-2z" fill="currentColor"/>
                </svg>
                Assigner tables
              </button>
            )}
          </div>

          {isExpanded && (
            <div className="invites-section">
              <div className="invites-header">
                <h4>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V6h16v12zM8 10h8v2H8v-2z" fill="currentColor"/>
                  </svg>
                  Liste des invités
                </h4>
                <div className="invites-header-actions">
                  <div className="search-invites" style={{ position: 'relative', marginRight: 'auto' }}>
                    <input
                      type="text"
                      placeholder="🔍 Rechercher par nom, table ou téléphone..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{
                        padding: '8px 12px',
                        borderRadius: '8px',
                        border: '1px solid #ddd',
                        width: '250px',
                        fontSize: '14px'
                      }}
                    />
                    {searchTerm && (
                      <button
                        onClick={() => setSearchTerm("")}
                        style={{
                          position: 'absolute',
                          right: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#999'
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {!readOnly && canDelete && (
                    <>
                      <button 
                        className="add-invite-btn"
                        onClick={() => setSelectedMariage(m)}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" fill="currentColor"/>
                        </svg>
                        Ajouter des invités
                      </button>
                      {currentUserRole === "megaadmin" && (
                        <button 
                          className="delete-all-btn"
                          onClick={() => deleteAllInvites(m._id)}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                          </svg>
                          Supprimer tous
                        </button>
                      )}
                    </>
                  )}
                  {mariageInvites.length > 0 && (
                    <button 
                      className="boissons-stats-btn"
                      onClick={() => toggleBoissonsStats(m._id)}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 3H4v14h14v-7h2c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 5h-2V5h2v3zM2 21h18v-2H2v2z" fill="currentColor"/>
                      </svg>
                      {showStats ? 'Masquer stats' : 'Voir totaux boissons'}
                    </button>
                  )}
                  {showStats && (
                    <button 
                      className="export-pdf-btn"
                      onClick={() => exportStatsToPDF(m, mariageInvites, localBoissonsStats)}
                      style={{
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        fontSize: '0.875rem',
                        fontWeight: '500',
                        height: '40px'
                      }}
                    >
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4V4c0-1.1-.9-2-2-2z" fill="currentColor"/>
                      </svg>
                      Exporter PDF
                    </button>
                  )}
                </div>
              </div>
              
              {isLoading ? (
                <div className="loading-invites">
                  <p>Chargement des invités...</p>
                </div>
              ) : mariageInvites.length === 0 ? (
                <div className="empty-invites">
                  <p>Aucun invité pour ce mariage.</p>
                  <p>Utilisez le bouton "Ajouter des invités" pour importer une liste.</p>
                </div>
              ) : (
                <div className="invites-list-container">
                  {searchTerm && (
                    <div style={{ marginBottom: '15px', fontSize: '14px', color: '#666' }}>
                      {invitesToShow.length} résultat(s) trouvé(s) sur {mariageInvites.length}
                    </div>
                  )}
                  
                  {showStats && (
                    <div className="boissons-stats-container">
                      <h5>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 3H4v14h14v-7h2c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 5h-2V5h2v3zM2 21h18v-2H2v2z" fill="currentColor"/>
                        </svg>
                        Statistiques des boissons
                      </h5>
                      <div className="boissons-totals">
                        <div className="total-item">
                          <span className="total-label">Total invités :</span>
                          <span className="total-value">{mariageInvites.length}</span>
                        </div>
                        <div className="total-item">
                          <span className="total-label">Avec choix de boisson :</span>
                          <span className="total-value">{totalAvecChoix} ({pourcentageAvecChoix}%)</span>
                        </div>
                        <div className="total-item">
                          <span className="total-label">Sans choix de boisson :</span>
                          <span className="total-value">{totalSansChoix}</span>
                        </div>
                      </div>
                      
                      {localBoissonsStats.length > 0 ? (
                        <div className="boissons-details">
                          <h6>Totaux par boisson :</h6>
                          <div className="boissons-details-grid">
                            {localBoissonsStats.map((item) => (
                              <div key={item.boisson} className="boisson-detail-item">
                                <span className="boisson-detail-name">{item.boisson}</span>
                                <span className="boisson-detail-count">{item.count} personne(s)</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="no-boissons-stats">
                          <p>Aucune boisson sélectionnée par les invités pour le moment.</p>
                        </div>
                      )}
                    </div>
                  )}
                  
                  <div className="invites-stats">
                    <span className="stat">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M16 11c1.66 0 2.99-1.34 2.99-3S17.66 5 16 5c-1.66 0-3 1.34-3 3s1.34 3 3 3zm-8 0c1.66 0 2.99-1.34 2.99-3S9.66 5 8 5C6.34 5 5 6.34 5 8s1.34 3 3 3zm0 2c-2.33 0-7 1.17-7 3.5V19h14v-2.5c0-2.33-4.67-3.5-7-3.5zm8 0c-.29 0-.62.02-1 .05 1.16.84 2 1.87 2 3.45V19h6v-2.5c0-2.33-4.67-3.5-7-3.5z" fill="currentColor"/>
                      </svg>
                      {searchTerm ? `${invitesToShow.length} / ${mariageInvites.length}` : `Total: ${mariageInvites.length}`}
                    </span>
                    <span className="stat">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z" fill="currentColor"/>
                      </svg>
                      Couples: {mariageInvites.filter(i => i.couple === 'oui').length}
                    </span>
                    <span className="stat">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 12c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" fill="currentColor"/>
                      </svg>
                      Individus: {mariageInvites.filter(i => i.couple === 'non').length}
                    </span>
                    <span className="stat">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41L9 16.17z" fill="currentColor"/>
                      </svg>
                      Confirmés: {mariageInvites.filter(i => i.statut === 'confirmed').length}
                    </span>
                    <span className="stat">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12 19 6.41z" fill="currentColor"/>
                      </svg>
                      Déclinés: {mariageInvites.filter(i => i.statut === 'declined').length}
                    </span>
                  </div>
                  
                  <div className="invites-table">
                    <table className="invites-table-inner">
                      <thead>
                        <tr>
                          <th>Nom</th>
                          <th>Table</th>
                          <th>Type</th>
                          <th>Statut</th>
                          <th>Lien d'invitation</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {invitesToShow.map((invite) => {
                          const invitationLink = generateInvitationLink(invite.uniqueToken);
                          const emailSubject = `Invitation au mariage de ${m.bride} & ${m.groom}`;
                          const emailBody = `Bonjour ${invite.nom},\n\nVous êtes invité au mariage de ${m.bride} & ${m.groom}.\n\nLien d'invitation: ${invitationLink}\n\nDate: ${m.date}\nHeure: ${m.heure}`;
                          
                          return (
                            <tr key={invite._id}>
                              <td>{invite.nom}</td>
                              <td>{invite.table || '-'}</td>
                              <td>
                                <span className={`couple-badge ${invite.couple}`}>
                                  {invite.couple === 'oui' ? 'Couple' : 'Individuel'}
                                </span>
                              </td>
                              <td>
                                <span 
                                  className="status-badge"
                                  style={{ backgroundColor: getStatusColor(invite.statut) }}
                                >
                                  {getStatusText(invite.statut)}
                                </span>
                              </td>
                              <td className="link-cell">
                                <div className="link-container">
                                  <input 
                                    type="text" 
                                    value={invitationLink} 
                                    readOnly 
                                    className="link-input"
                                  />
                                  <button 
                                    className="copy-btn"
                                    onClick={() => copyToClipboard(invitationLink)}
                                    title="Copier le lien"
                                  >
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                      <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
                                    </svg>
                                  </button>
                                </div>
                              </td>
                              <td className="actions-cell">
                                {!readOnly && (
                                  <>
                                    {(currentUserRole === "super_admin" || currentUserRole === "megaadmin") && (
                                      <button 
                                        className="edit-btn"
                                        onClick={() => openEditInvite(invite, m._id)}
                                        title="Modifier cet invité"
                                        style={{
                                          backgroundColor: '#ffc107',
                                          color: '#000',
                                          border: 'none',
                                          borderRadius: '4px',
                                          padding: '8px',
                                          cursor: 'pointer',
                                          marginRight: '5px'
                                        }}
                                      >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34c-.39-.39-1.02-.39-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" fill="currentColor"/>
                                        </svg>
                                      </button>
                                    )}
                                    
                                    <button 
                                      className={`share-btn whatsapp ${invite.lienEnvoye ? 'sent' : ''}`}
                                      onClick={() => shareViaWhatsApp(
                                        invite.telephone, 
                                        invitationLink, 
                                        invite.nom, 
                                        m._id, 
                                        invite._id,
                                        m
                                      )}
                                      title={invite.lienEnvoye ? "Déjà envoyé" : "Envoyer sur WhatsApp"}
                                      disabled={invite.lienEnvoye}
                                      style={{ 
                                        backgroundColor: invite.lienEnvoye ? '#6c757d' : '#25D366',
                                        opacity: invite.lienEnvoye ? 0.6 : 1,
                                        cursor: invite.lienEnvoye ? 'not-allowed' : 'pointer',
                                        marginRight: '5px'
                                      }}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M19.05 4.91C17.18 3.24 14.81 2.2 12.2 2.04 5.95 1.56 2.2 6.38 2.2 9.4c0 1.4.5 2.7 1.3 3.8L3 18.9l5.8-1.6c1.2.7 2.5 1.1 3.9 1.1 5.3 0 9.1-4.8 9.1-9.1 0-2.4-1-4.7-2.8-6.4z" fill="currentColor"/>
                                      </svg>
                                    </button>
                                    <button 
                                      className="share-btn email"
                                      onClick={() => shareViaEmail(emailSubject, emailBody)}
                                      title="Envoyer par email"
                                      style={{ marginRight: '5px' }}
                                    >
                                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" fill="currentColor"/>
                                      </svg>
                                    </button>
                                    {canDelete && currentUserRole === "megaadmin" && (
                                      <button 
                                        className="delete-btn"
                                        onClick={() => deleteInvite(invite._id, m._id)}
                                        title="Supprimer cet invité"
                                      >
                                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                          <path d="M6 19c0 1.1.9 2 2 2h8c1.1 0 2-.9 2-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z" fill="currentColor"/>
                                        </svg>
                                      </button>
                                    )}
                                  </>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  
                  {!readOnly && canDelete && (
                    <div className="bulk-actions">
                      <h5>Actions groupées :</h5>
                      <div className="bulk-buttons">
                        <button 
                          className="bulk-btn copy-all"
                          onClick={() => {
                            const allLinks = mariageInvites
                              .map(invite => `${invite.nom}: ${generateInvitationLink(invite.uniqueToken)}`)
                              .join('\n');
                            copyToClipboard(allLinks);
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12V1zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2zm0 16H8V7h11v14z" fill="currentColor"/>
                          </svg>
                          Copier tous les liens
                        </button>
                        <button 
                          className="bulk-btn export"
                          onClick={() => {
                            const csvContent = "Nom,Table,Type,Statut,Lien,Telephone,LienEnvoye\n" +
                              mariageInvites.map(invite => 
                                `"${invite.nom}","${invite.table}","${invite.couple === 'oui' ? 'Couple' : 'Individuel'}","${getStatusText(invite.statut)}","${generateInvitationLink(invite.uniqueToken)}","${invite.telephone || ''}","${invite.lienEnvoye ? 'Oui' : 'Non'}"`
                              ).join('\n');
                            
                            const blob = new Blob([csvContent], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `invites-${m.bride}-${m.groom}.csv`;
                            a.click();
                          }}
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z" fill="currentColor"/>
                          </svg>
                          Exporter en CSV
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        
        <div
          className="color-zone"
          style={{ backgroundColor: getCardColor(m.dateISO) }}
        >
          <span className="date-indicator">
            {m.dateISO ? new Date(m.dateISO).toLocaleDateString('fr-FR') : 'Date inconnue'}
          </span>
        </div>
      </li>
    );
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="muted">Chargement des mariages...</p>
      </div>
    );
  }

  return (
    <section className="mariage-section">
      <div className="section-header">
        <h3 className="sujet">Liste des Mariages</h3>
        {!readOnly && (
          <button 
            className="refresh-btn"
            onClick={fetchMariages}
            disabled={loading}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4c-4.42 0-7.99 3.58-7.99 8s3.57 8 7.99 8c3.73 0 6.84-2.55 7.73-6h-2.08c-.82 2.33-3.04 4-5.65 4-3.31 0-6-2.69-6-6s2.69-6 6-6c1.66 0 3.14.69 4.22 1.78L13 11h7V4l-2.35 2.35z" fill="currentColor"/>
            </svg>
            Rafraîchir
          </button>
        )}
      </div>

      <ul className="mariage-list">
        {mariages.length === 0 ? (
          <div className="empty-state">
            <p className="muted">Aucun mariage enregistré pour l'instant.</p>
          </div>
        ) : (
          mariages.map(renderMariageItem)
        )}
      </ul>

      {selectedMariage && !readOnly && (
        <MariageOptionsModal
          isOpen={!!selectedMariage}
          onClose={() => setSelectedMariage(null)}
          mariage={selectedMariage}
          onUpdated={() => {
            fetchMariages();
            if (expandedMariage === selectedMariage._id) {
              fetchInvites(selectedMariage._id);
            }
          }}
        />
      )}

      {renderEditModal()}
      {renderDemandeModal()}
    </section>
  );
};

export default MariageList;